"use client";

import { useEffect, useRef } from "react";
import { FocusTrap, FocusTrapFeatures, PopoverButton } from "@headlessui/react";
import cn from "classnames";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import { GlobalNavigationMenuItem } from "../GlobalNavigation";
import Link from "@/components/Link";
import { getHrefFromCustomUrlPlugin } from "@/helpers";

export type HeaderNavigationPanelProps = {
  panelColor?: keyof typeof PANEL_COLOR_MAPPING;
  closeMenu?: () => void;
  index: number;
} & GlobalNavigationMenuItem;

const PANEL_COLOR_MAPPING = {
  "blue-500": "bg-swdc-blue-500",
  "cool-gray-700": "bg-swdc-cool-gray-700",
  "gray-500": "bg-swdc-gray-500",
};

const HeaderNavigationPanel: React.FC<HeaderNavigationPanelProps> = ({
  closeMenu,
  panelColor = "cool-gray-700",
  index,
  ...props
}) => {
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const catDescriptionRef = useRef<HTMLDivElement | null>(null);
  const isLeftSection =
    props?.leftContent ||
    props?.leftCta ||
    props?.leftLinksTitle ||
    props?.leftLinks;

  useEffect(() => {
    if (descriptionRef.current && closeMenu) {
      const links = descriptionRef.current.querySelectorAll("a");
      links.forEach((link) => {
        link.onclick = closeMenu;
      });
    }
    if (catDescriptionRef.current && closeMenu) {
      const links = catDescriptionRef.current.querySelectorAll("a");
      links.forEach((link) => {
        link.onclick = closeMenu;
      });
    }
  }, [closeMenu]);

  return (
    <FocusTrap features={FocusTrapFeatures.FocusLock}>
      <div
        className="mx-auto -mt-0.75 flex max-h-[85dvh] flex-col overflow-y-auto shadow-[0_20px_40px_0_rgba(0,0,0,0.2)]"
        {...getDataAutomationId(`navigation-dropdown-${index}-block`)}
        {...getDataAutomationGroup("panel")}
      >
        <div className="flex flex-row">
          {isLeftSection && (
            <div
              className={cn(
                "w-100 p-8 text-white",
                PANEL_COLOR_MAPPING[panelColor],
              )}
            >
              {props?.leftContent}
              {props?.leftCta}
              {props?.leftLinksTitle && (
                <div className="text-body-2xs tracking-swdc-wide mb-2 border-b-1 border-solid border-white pb-2 leading-4 font-bold">
                  {props.leftLinksTitle.toUpperCase()}
                </div>
              )}
              <div className="flex flex-col gap-y-3">
                {props?.leftLinks?.map(({ title, href, dld, dlt }, index) => (
                  <PopoverButton
                    key={`${title}-${index}`}
                    as={Link}
                    className="text-link-s active:text-swdc-cool-gray-400 hover:underline active:underline"
                    href={getHrefFromCustomUrlPlugin(href)}
                    {...getDataLinkAttributes({
                      dlt: dlt,
                      dld: dld,
                    })}
                    {...getDataAutomationId(
                      `navigation-dropdown-${index}-explore-link`,
                    )}
                  >
                    {title}
                  </PopoverButton>
                ))}
              </div>
            </div>
          )}
          <div
            className={cn(
              isLeftSection ? "w-208" : "w-full",
              "bg-swdc-cool-gray-200 gap-8 p-8",
              props?.categoryLinksInTwoColumns ? "columns-2" : "flex flex-col",
            )}
          >
            {props?.categoryLinks?.map(
              (
                {
                  icon,
                  categoryTitle,
                  categoryDescription,
                  links,
                  cta,
                  linksColumnCount,
                },
                index,
              ) => (
                <div
                  key={`${categoryTitle}-${index}`}
                  className={cn(
                    "flex break-inside-avoid flex-row",
                    props?.categoryLinksInTwoColumns && "mb-10",
                  )}
                >
                  {icon}
                  <div className="flex w-full break-inside-avoid flex-col pl-4">
                    <div
                      className="text-swdc-black-600 text-subhead-2xs font-bold"
                      {...getDataAutomationId(
                        `navigation-dropdown-${index}-category-title`,
                      )}
                    >
                      {categoryTitle}
                    </div>
                    {categoryDescription}
                    <div
                      className={cn(
                        "grid w-full gap-3 pt-4",
                        linksColumnCount === "1" && "grid-cols-1",
                        linksColumnCount === "2" && "grid-cols-2",
                        linksColumnCount === "3" && "grid-cols-3",
                        !linksColumnCount && "flex flex-row gap-x-20",
                      )}
                    >
                      {links?.map(({ title, href, dld, dlt }, index) => (
                        <PopoverButton
                          key={`${title}-${index}`}
                          as={Link}
                          className="text-link-s text-swdc-black-600 site-support:text-swdc-gray-400 active:text-tab-link-pressed hover:underline active:underline"
                          href={getHrefFromCustomUrlPlugin(href)}
                          {...getDataLinkAttributes({
                            dlt: dlt,
                            dld: dld,
                          })}
                          {...getDataAutomationId(
                            `navigation-dropdown-${index}-link`,
                          )}
                        >
                          {title}
                        </PopoverButton>
                      ))}
                    </div>
                    {cta}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
        {!!props?.bottomLinksTitle && !!props?.bottomLinks?.length && (
          <div className="bg-swdc-black-600 flex flex-row items-center gap-x-8 px-6 py-3 text-white">
            <div className="text-body-m mr-8 font-bold">
              {props.bottomLinksTitle}
            </div>

            {props?.bottomLinks?.map(({ title, href, dld, dlt }, index) => (
              <PopoverButton
                key={`${title}-${index}`}
                as={Link}
                className="text-link-s active:text-swdc-orange-400 hover:underline active:underline"
                href={getHrefFromCustomUrlPlugin(href)}
                {...getDataLinkAttributes({
                  dlt: dlt,
                  dld: dld,
                })}
                {...getDataAutomationId(
                  `navigation-dropdown-${index}-bottom-link`,
                )}
              >
                {title}
              </PopoverButton>
            ))}
          </div>
        )}
      </div>
    </FocusTrap>
  );
};

export default HeaderNavigationPanel;
